import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from '../baseStore';
import { RootStore } from '../rootStore';
import { LenderStatementHeadlinesService } from '/src/api/services';
import { LenderStatementHeadlines } from '/src/lib/models';
import { TGetLenderStatementHeadlinesRequest } from '/src/lib/types';

export interface LenderStatementHeadlinesStoreProps {
  /**
   * Array of Lender Statement Headlines
   */
  lenderStatementHeadlines?: LenderStatementHeadlines;
}

export class LenderStatementHeadlinesStore extends BaseStore implements LenderStatementHeadlinesStoreProps {
  lenderStatementHeadlines?: LenderStatementHeadlines;

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      lenderStatementHeadlines: observable,
    });
  }

  /**** Data Processing ****/

  /**
   * Formats Lender Statement Headlines response.
   *
   * @param response - Headlines to format
   * @returns The formatted Headlines
   */
  private formatLenderStatementHeadlines = (response: LenderStatementHeadlines) => {
    const headlines = response;
    let balances = [];

    // End-of-month calcs
    if (headlines.month) {
      headlines.month.totalRevenue = headlines.month.appliedToFees + headlines.month.appliedToInterest;
      headlines.month.netRevenue = headlines.month.totalRevenue - headlines.month.managementFees;
      headlines.month.totalUnpaidRevenue = headlines.month.feeBalance + headlines.month.interestBalance;
      balances = [
        headlines.month.account.amountInvested,
        -Math.abs(headlines.month.principalBalance),
        headlines.month.appliedToFees,
        headlines.month.appliedToInterest,
        -Math.abs(headlines.month.managementFees),
        -Math.abs(headlines.month.account.amountReturned ?? 0),
      ];
      headlines.month.balances = balances.reduce((prev, curr) => (prev ?? 0) + (curr ?? 0)) ?? 0;
    }

    // YTD calcs
    if (headlines.ytd) {
      headlines.ytd.totalRevenue = headlines.ytd.appliedToFees + headlines.ytd.appliedToInterest;
      headlines.ytd.netRevenue = headlines.ytd.totalRevenue - headlines.ytd.managementFees;
      headlines.ytd.totalUnpaidRevenue = headlines.ytd.feeBalance + headlines.ytd.interestBalance;
      balances = [
        headlines.ytd.account.amountInvested,
        -Math.abs(headlines.ytd.principalBalance),
        headlines.ytd.appliedToFees,
        headlines.ytd.appliedToInterest,
        -Math.abs(headlines.ytd.managementFees),
        -Math.abs(headlines.ytd.account.amountReturned ?? 0),
      ];
      headlines.ytd.balances = balances.reduce((prev, curr) => (prev ?? 0) + (curr ?? 0)) ?? 0;
    }

    // All-time calcs
    if (headlines.alltime) {
      headlines.alltime.totalRevenue = headlines.alltime.appliedToFees + headlines.alltime.appliedToInterest;
      headlines.alltime.netRevenue = headlines.alltime.totalRevenue - headlines.alltime.managementFees;
      headlines.alltime.totalUnpaidRevenue = headlines.alltime.feeBalance + headlines.alltime.interestBalance;
      balances = [
        headlines.alltime.account.amountInvested,
        -Math.abs(headlines.alltime.principalBalance),
        headlines.alltime.appliedToFees,
        headlines.alltime.appliedToInterest,
        -Math.abs(headlines.alltime.managementFees),
        -Math.abs(headlines.alltime.account.amountReturned ?? 0),
      ];
      headlines.alltime.balances = balances.reduce((prev, curr) => (prev ?? 0) + (curr ?? 0)) ?? 0;
      headlines.alltime.holdingAccountBalance =
        headlines.alltime.account.balance - headlines.alltime.principalBalance + headlines.alltime.netRevenue || 0;
    }

    // Return the data
    return headlines;
  };

  /**** Lender Statement Headlines CRUD ****/

  /**
   * Fetches and caches Lender Statement Headlines data.
   *
   * @param filters - Filters to use when fetching Headlines
   * @returns The Lender Statement Headlines
   */
  get = async (filters: TGetLenderStatementHeadlinesRequest) => {
    const lenderStatementHeadlines = await LenderStatementHeadlinesService.get(filters);

    runInAction(() => (this.lenderStatementHeadlines = this.formatLenderStatementHeadlines(lenderStatementHeadlines)));

    return this.lenderStatementHeadlines;
  };
}
